/* ============================================================================
   RVRN Builder — 共享的外部链接 CTA 绑定（直接跳转）+ 可选 iframe 弹窗桥
   ----------------------------------------------------------------------------
   作用：任何 section 的 CTA 只要渲染出 data-builder-external-url / data-builder-external-target，
   再调用 RVRNBuilder.bindExternalCtas(rootScope) 接管点击：
     · _self（默认）：当前标签顶层跳转到该外部地址；
     · _blank：新标签打开。

   数据交付：POST 表单提交（application/x-www-form-urlencoded），字段与旧 iframe 的
   postMessage payload 一致：
     { type:'RVRN_VEHICLE', user_id, customer_id, logged_in, year, make, model }
   ⚠ 服务端前置：静态 .html 只接受 GET/HEAD，直接 POST 会 405；builder 侧需要一个
   POST 入口控制器（照抄 ProductRidePreviewEntryController：把参数归一成 query 后 302
   到静态页），静态页再用 uni/assets/wheel-message.js#readUrlQuery 读取。

   为什么从"iframe 弹窗"改成"直接跳转"：目标页（builder / step6-ride）会做强制跳转
   （登录/授权），而 Shopify 登录页不允许被 iframe 嵌套 —— 在弹窗里会白屏或被拦，
   只有顶层窗口才走得通。

   用法（section 侧）：
     1) 给 CTA 渲染时附加 data-builder-external-url / data-builder-external-target
     2) 引入本文件 + 调用 RVRNBuilder.bindExternalCtas(rootScope)

   保留但当前无调用方（仅供将来回退成内嵌弹窗时使用）：
     · RVRNBuilder.openIframe(baseUrl, ymm) —— iframe 弹窗 + 加载后 postMessage 车型/身份；
     · 下方 RVRN_LOGIN_REQUIRED 顶层跳转处理（配合 iframe 内的登录请求）。

   对外暴露：window.RVRNBuilder = { bindExternalCtas, getExternalUrl, openIframe, getUserId,
                                  getCustomerId, readVehicle }
   ========================================================================== */
(function () {
  'use strict';

  function RVRNBuilder() {}

  /* 最近一次打开的 builder iframe 的 origin：用于校验子窗口发来的消息来源 */
  var lastFrameOrigin = '';

  /* 允许子窗口请求的顶层跳转目标前缀（登录中转页）。白名单校验，防止被嵌入内容诱导跳转。
     前缀列表，新增中转页时必须在这里登记：
       ① /pages/login-bridge*        —— 通用桥（含 -builder 等来源后缀）
       ② /pages/4sales-preview-bridge —— 销售 Ride Preview 专用桥（页面名自带业务前缀，
                                          不落在 ① 的前缀下，必须单列，否则会被白名单拒绝） */
  var LOGIN_BRIDGE_PREFIXES = [
    'https://rvrnwheel.com/pages/login-bridge',
    'https://rvrnwheel.com/pages/4sales-preview-bridge'
  ];

  /* ----- 读取当前车型：localStorage 优先，回退 URL ?vehicle= ----- */
  RVRNBuilder.readVehicle = function () {
    var STORAGE_KEY = 'rvrn_customer_vehicle';
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && typeof parsed.value === 'string' && parsed.value.trim()) {
          return parsed.value.trim();
        }
      }
    } catch (e) {
      // fall through to URL param
    }
    var params = new URLSearchParams(window.location.search);
    return params.get('vehicle') || '';
  };

  /* ----- 把 "2022 Toyota Camry" 拆成 year/make/model ----- */
  RVRNBuilder.parseYearMakeModel = function (value) {
    if (!value) return null;
    var parts = value.trim().split(/\s+/);
    if (parts.length < 3) return null;
    var year = parts[0];
    var model = parts[parts.length - 1];
    var make = parts.slice(1, parts.length - 1).join(' ');
    return { year: year, make: make, model: model };
  };

  /* ----- 获取 Shopify 用户 ID：登录态 customer.id 优先，回退匿名 _shopify_y -----
     取值口径与 assets/product-options.js#getLoggedInCustomerId 保持一致：
     隐藏 input（Liquid 注入 customer.id）优先，window.__customerId 兼容。 */
  RVRNBuilder.getUserId = function () {
    var hiddenInput = document.getElementById('656565115116111114101');
    var customerId =
      (hiddenInput && hiddenInput.getAttribute('data-customer-id')) || window.__customerId || null;
    if (customerId) return String(customerId);
    var match = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  /* ----- 仅在【已登录】时返回顾客 ID（没有匿名 _shopify_y 兜底）-----
     为什么要单独一个函数：getUserId() 未登录时会回退匿名 _shopify_y，builder 侧
     无法据此区分「真登录」与「匿名访客」。登录判定必须用本函数：
     有值 = 登录态；空串 = 未登录（含匿名）。同时 postMessage 给 builder 的
     payload 里用 customer_id / logged_in 两个字段把这件事说清楚。 */
  RVRNBuilder.getCustomerId = function () {
    var hiddenInput = document.getElementById('656565115116111114101');
    return String(
      (hiddenInput && hiddenInput.getAttribute('data-customer-id')) || window.__customerId || ''
    );
  };

  function safeOrigin(url) {
    try {
      return new URL(url).origin;
    } catch (e) {
      return '*';
    }
  }

  /* ----- 打开 iframe 弹窗，并在加载后 postMessage 车型/用户数据 -----
     注意：CTA 已改为直接跳转（见 bindExternalCtas），这里是保留的备用入口。 */
  RVRNBuilder.openIframe = function (baseUrl, ymm) {
    if (document.querySelector('.rvrn-builder__iframe-modal')) return;

    // 记录本次 iframe 的来源，供 onClose / 登录顶层跳转的消息校验使用
    lastFrameOrigin = safeOrigin(baseUrl);

    var overlay = document.createElement('div');
    overlay.className = 'rvrn-builder__iframe-modal';

    var frame = document.createElement('iframe');
    frame.className = 'rvrn-builder__iframe';
    frame.src = baseUrl;
    frame.setAttribute('title', 'RVRN External Content');
    frame.setAttribute('allow', 'fullscreen');

    overlay.appendChild(frame);
    document.body.appendChild(overlay);
    document.body.classList.add('rvrn-builder-iframe-open');

    /* 强制关闭 + 释放资源：先卸载 iframe 页面（停止 WebGL/定时器），再移除节点 */
    var close = function () {
      try {
        if (frame && frame.parentNode) {
          frame.src = 'about:blank';
          frame.remove();
          frame = null;
        }
      } catch (e) {
        console.warn('[RVRN Builder] failed to unload iframe', e);
      }
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.body.classList.remove('rvrn-builder-iframe-open');
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onMessage);
    };

    /* onClose 走标准 postMessage 双向通道（父窗口只读 message 事件，不做跨域 JS 注入）：
        子窗口(builder)：window.parent.postMessage({ type:'onClose', ts }, parentOrigin)
        父窗口(本文件)： window 监听 message，校验 type/origin 后关闭 iframe */
    var onKey = function (e) {
      if (e.key === 'Escape') close();
    };
    var onMessage = function (e) {
      if (!e.data || typeof e.data !== 'object' || e.data.type !== 'onClose') return;
      var expected = safeOrigin(baseUrl);
      if (expected && expected !== '*' && e.origin !== expected) {
        console.warn('[RVRN Builder] onClose ignored: origin mismatch', e.origin, '!==', expected);
        return;
      }
      close();
    };

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
    document.addEventListener('keydown', onKey);
    // postMessage 的 message 事件派发在 window 上，必须挂 window 才能收到
    window.addEventListener('message', onMessage);

    var postVehicle = function () {
      var targetOrigin = safeOrigin(baseUrl);
      // customer_id / logged_in：只有真登录才有值。builder 侧据此判断"要不要去登录"——
      // 不能只看 user_id，因为未登录时它是匿名的 _shopify_y。
      var customerId = RVRNBuilder.getCustomerId();
      var payload = {
        type: 'RVRN_VEHICLE',
        user_id: RVRNBuilder.getUserId(),
        customer_id: customerId,
        logged_in: !!customerId,
        year: ymm ? ymm.year : '',
        make: ymm ? ymm.make : '',
        model: ymm ? ymm.model : ''
      };
      try {
        if (frame.contentWindow) {
          frame.contentWindow.postMessage(payload, targetOrigin);
        }
      } catch (e) {
        // ignore post errors
      }
    };

    if (frame.contentWindow && frame.contentWindow.postMessage) {
      frame.addEventListener('load', postVehicle);
    } else {
      postVehicle();
    }
  };

  /* ----- 读取 CTA 上的外部 URL：空属性/无元素一律返回空串 ----- */
  RVRNBuilder.getExternalUrl = function (cta) {
    if (!cta) return '';
    return (cta.getAttribute('data-builder-external-url') || '').trim();
  };

  /* ----- 组装要交给外部页面（builder）的数据 -----
     字段与旧 iframe 弹窗 postMessage 的 payload 完全一致：
       { type:'RVRN_VEHICLE', user_id, customer_id, logged_in, year, make, model }
     user_id 取登录态 customer.id，未登录回退匿名 _shopify_y（同 getUserId 口径）。 */
  RVRNBuilder.buildVehiclePayload = function () {
    var ymm = RVRNBuilder.parseYearMakeModel(RVRNBuilder.readVehicle());
    var customerId = RVRNBuilder.getCustomerId();
    return {
      type: 'RVRN_VEHICLE',
      user_id: RVRNBuilder.getUserId(),
      customer_id: customerId,
      logged_in: !!customerId,
      year: ymm ? ymm.year : '',
      make: ymm ? ymm.make : '',
      model: ymm ? ymm.model : ''
    };
  };

  /* ----- 用 POST 表单把数据提交到外部地址（顶层导航；_blank 时新标签 POST）-----
     ⚠ 前置条件（服务端）：Spring 静态资源处理器只放行 GET/HEAD，直接 POST 到静态
     .html 会 405。builder 侧必须先有一个 POST 入口控制器，把表单参数归一成 query 串后
     302 到静态页（照抄 vehicle/ProductRidePreviewEntryController 的做法），
     静态页再用 uni/assets/wheel-message.js#readUrlQuery 读查
     （user_id / customer_id / year / make / model）。
     为什么要 POST：数据放在请求体里，不占 URL 长度、不落在地址栏/Referer。
     表单与 fetch 不同：这是一次真实的顶层导航（浏览器会把响应渲染出来），
     目标页的强制跳转（登录）不会被同源策略拦住。 */
  RVRNBuilder.submitPost = function (url, payload, target) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = url;
    form.target = target === '_blank' ? '_blank' : '_self';
    form.acceptCharset = 'UTF-8';
    form.style.display = 'none';

    Object.keys(payload).forEach(function (key) {
      var value = payload[key];
      if (value === undefined || value === null) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    try {
      form.submit();
    } finally {
      setTimeout(function () { form.remove(); }, 1000);
    }
  };

  /* ----- 绑定 scope 下所有带 data-builder-external-url 的 CTA -----
     行为：点击后以【POST 表单】把车型/身份数据提交到该外部地址（不再用 iframe 弹窗）——
     目标页（builder / step6-ride）会做强制跳转（登录/授权），而 Shopify 登录页不允许被
     iframe 嵌套，在弹窗里会白屏或被拦；顶层导航才走得通。
     数据交付：POST body（见 buildVehiclePayload 的字段），URL 保持后台配置的原样
     （如 ?auto=0#ride 不受影响）。
     target 语义照旧：data-builder-external-target="_self"（默认）= 当前标签 POST，
     "_blank" = 新标签 POST。
     ⚠ server 侧需先支持 POST（见 submitPost 注释），否则静态 .html 会返回 405。 */
  RVRNBuilder.bindExternalCtas = function (scope) {
    var root = scope || document;
    root.querySelectorAll('[data-builder-external-url]').forEach(function (cta) {
      if (cta.dataset.rvrnBuilderBound === 'true') return;
      cta.dataset.rvrnBuilderBound = 'true';
      cta.addEventListener('click', function (event) {
        // 1) 先读：空属性直接放行，绝不 preventDefault（避免「点了没反应」）
        var baseUrl = RVRNBuilder.getExternalUrl(cta);
        if (!baseUrl) return;
        // 2) 修饰键 / 非左键点击不接管，交给浏览器默认行为（新标签、下载等）
        if (event.button !== 0 || event.metaKey || event.ctrlKey ||
            event.shiftKey || event.altKey) return;
        // 3) 显式接管：POST 到 data 属性里的地址（可能与 <a href> 不同，老配置以属性为准）
        event.preventDefault();
        var payload = RVRNBuilder.buildVehiclePayload();
        var target = (cta.getAttribute('data-builder-external-target') || '_self').trim();
        console.log('[RVRN Builder] external CTA -> POST', target, baseUrl, payload);
        RVRNBuilder.submitPost(baseUrl, payload, target === '_blank' ? '_blank' : '_self');
      });
    });
  };

  /* ----- 子窗口请求「顶层跳转」（登录中转）-----
     为什么必须由父窗口执行：跨源 iframe 自己跳转会白屏 —— Shopify 托管登录页不允许被嵌套。
     builder 在需要登录时发 { type:'RVRN_LOGIN_REQUIRED', url }，这里做顶层导航。
     两道校验：来源必须是最近打开的 builder iframe；url 必须落在登录中转页白名单前缀内。 */
  window.addEventListener('message', function (e) {
    var d = e.data;
    if (!d || typeof d !== 'object' || d.type !== 'RVRN_LOGIN_REQUIRED') return;
    if (!lastFrameOrigin || e.origin !== lastFrameOrigin) {
      console.warn('[RVRN Builder] login request ignored: origin mismatch', e.origin, '!==', lastFrameOrigin);
      return;
    }
    var url = String(d.url || '');
    var allowed = LOGIN_BRIDGE_PREFIXES.some(function (prefix) {
      return url.indexOf(prefix) === 0;
    });
    if (!allowed) {
      console.warn('[RVRN Builder] login request ignored: url not allowed', url, LOGIN_BRIDGE_PREFIXES);
      return;
    }
    window.location.href = url;
  });

  window.RVRNBuilder = RVRNBuilder;
})();
